// BurstStorageTable.jsx
// Small read-only table that shows per-pod burst storage usage when
// available. Each pod may include a `burst_status` object with fields:
// { enabled, mount_path, used_gb, capacity_gb }.
//
// The component renders a responsive small table with columns: Pod
// Name, Mount Path, and Usage (text + progress bar). Pods without
// burst storage enabled are shown as N/A.
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";
import {
  FONT,
  INK,
  INK_MUTED,
  ACCENT,
  HAIRLINE,
} from "../../../../theme/tokens";

/**
 * BurstStorageTable
 * props:
 * - pods: array of pod objects (each may contain burst_status)
 */
export default function BurstStorageTable({ pods = [] }) {
  if (!pods.length) {
    return (
      <Typography sx={{ fontSize: FONT.sm, color: INK_MUTED }}>
        No pods available.
      </Typography>
    );
  }

  return (
    <TableContainer
      sx={{
        m: 0,
        boxShadow: "none",
        border: `1px solid ${HAIRLINE}`,
        borderRadius: 2,
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Pod Name</TableCell>
            <TableCell>Mount Path</TableCell>
            <TableCell>Usage</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pods.map((pod) => {
            const burstStorage = pod.burst_status || { enabled: false };
            if (!burstStorage.enabled) {
              return (
                <TableRow key={pod.name}>
                  <TableCell>{pod.name}</TableCell>
                  <TableCell colSpan={2}>N/A</TableCell>
                </TableRow>
              );
            }
            if (
              burstStorage.used_gb == null ||
              burstStorage.capacity_gb == null
            ) {
              return (
                <TableRow key={pod.name}>
                  <TableCell>{pod.name}</TableCell>
                  <TableCell>{burstStorage.mount_path}</TableCell>
                  <TableCell>Unavailable</TableCell>
                </TableRow>
              );
            }
            const percent =
              burstStorage.capacity_gb > 0
                ? (burstStorage.used_gb / burstStorage.capacity_gb) * 100
                : 0;
            return (
              <TableRow key={pod.name}>
                <TableCell>{pod.name}</TableCell>
                <TableCell>{burstStorage.mount_path}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography
                      sx={{
                        fontSize: FONT.xs,
                        color: INK,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {`${burstStorage.used_gb} GB / ${burstStorage.capacity_gb} GB`}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={percent}
                      sx={{
                        height: 6,
                        borderRadius: 999,
                        flex: 1,
                        minWidth: 120,
                        maxWidth: 250,
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                          backgroundColor: ACCENT,
                        },
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

BurstStorageTable.propTypes = {
  pods: PropTypes.arrayOf(PropTypes.object),
};
